import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Feature flags for security monitoring
const SECURITY_FEATURES_ENABLED = process.env.NODE_ENV === 'development';

// Import security functions only if features are enabled
let SESSION_TIMEOUT_MS: number = 24 * 60 * 60 * 1000; // 24 hours default
let checkSessionTimeout: ((lastActivity: number) => boolean) | undefined;
let generateSessionFingerprint: (() => string) | undefined;
let validateSessionIntegrity: ((fingerprint: string) => boolean) | undefined;
let detectSuspiciousActivity: ((activities: any[]) => any[]) | undefined;

if (SECURITY_FEATURES_ENABLED) {
  try {
    const securityModule = require('@/lib/security');
    SESSION_TIMEOUT_MS = securityModule.SESSION_TIMEOUT_MS;
    checkSessionTimeout = securityModule.checkSessionTimeout;
    generateSessionFingerprint = securityModule.generateSessionFingerprint;
    validateSessionIntegrity = securityModule.validateSessionIntegrity;
    detectSuspiciousActivity = securityModule.detectSuspiciousActivity;
  } catch (error) {
    console.warn('Security module not available, using defaults');
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  logout: () => Promise<void>;
  isDemo: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  logout: async () => {},
  isDemo: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error('useAuth must be used within an AuthProvider');
    // Return a safe default instead of throwing
    return {
      user: null,
      session: null,
      loading: false,
      logout: async () => {},
      isDemo: false,
    };
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [retryCount, setRetryCount] = useState(0);
  
  // SECURITY: demo mode fabricates a fake session, so it is only ever allowed in local dev.
  // In production ?demo is ignored entirely — no unauthenticated pseudo-session can be created.
  const isDemo = import.meta.env.DEV && new URLSearchParams(window.location.search).has('demo');


  // PRIMARY: Ensure a profile row exists for the authenticated user - IMMEDIATE, NO DELAYS
  const ensureProfileExists = async (u: User): Promise<boolean> => {
    try {
      // First check if profile exists
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', u.id)
        .maybeSingle();

      if (existingProfile) {
        return true;
      }

      // Profile missing - trigger should create it automatically
      console.warn('[Auth] Profile not found for user - will be created by trigger');
      return false;
    } catch (error) {
      console.error('[Auth] Profile check error:', error);
      return false;
    }
  };
  // Enhanced security: session fingerprinting and integrity checks (feature flagged)
  const initializeSessionSecurity = useCallback((session: Session) => {
    if (!SECURITY_FEATURES_ENABLED) return;
    
    try {
      // Simplified security initialization
      const activities = JSON.parse(localStorage.getItem('userActivities') || '[]');
      activities.push({
        timestamp: Date.now(),
        action: 'session_start',
        ip: '',
        userAgent: navigator.userAgent
      });
      
      // Keep only last 50 activities
      if (activities.length > 50) {
        activities.splice(0, activities.length - 50);
      }
      
      localStorage.setItem('userActivities', JSON.stringify(activities));
    } catch (error) {
      console.warn('Session security initialization failed:', error);
      // Don't block auth on security init failure
    }
  }, []);

  // Enhanced session integrity validation (feature flagged and more permissive during auth flow)
  const validateSession = useCallback((isInitialAuth = false) => {
    if (!SECURITY_FEATURES_ENABLED || !detectSuspiciousActivity) {
      return true; // Skip validation if security features disabled
    }
    
    try {
      // Skip all validation during initial auth to prevent false positives
      if (isInitialAuth) {
        return true;
      }
      
      // Be more lenient with suspicious activity detection during auth flow
      const activities = JSON.parse(localStorage.getItem('userActivities') || '[]');
      const alerts = detectSuspiciousActivity(activities);
      
      // Only logout on critical security issues, not during normal auth flow
      const criticalAlerts = alerts.filter(alert => 
        alert.severity === 'high' && 
        alert.type !== 'rapid_requests' && // Allow rapid requests during auth
        !isInitialAuth
      );
      
      if (criticalAlerts.length > 0) {
        console.warn('Critical security alert detected:', criticalAlerts);
        logout();
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Session validation error:', error);
      return true; // Don't block on validation errors
    }
  }, []);

  // PRIMARY: Force logout function with enhanced cleanup and cascade prevention
  const logout = useCallback(async () => {
    // Prevent multiple simultaneous logout calls
    if (sessionStorage.getItem('logout_in_progress') === 'true') {
      return;
    }
    
    try {
      sessionStorage.setItem('logout_in_progress', 'true');
      
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
      
      // Remove all authentication and security related data
      const keysToRemove = [
        'supabase.auth.token',
        'lastActivity', 
        'sessionFingerprint',
        'userActivities'
      ];
      
      // Remove Supabase auth tokens and security data
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key.startsWith('sb-') || keysToRemove.includes(key)) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('[Auth] Logout error:', error);
    } finally {
      sessionStorage.removeItem('logout_in_progress');
    }
  }, []);

  // Session timeout checker (feature flagged)
  useEffect(() => {
    if (!session || !SECURITY_FEATURES_ENABLED || !checkSessionTimeout) return;

    const checkTimeout = () => {
      const stored = localStorage.getItem('lastActivity');
      const lastAct = stored ? parseInt(stored, 10) : Date.now();
      
      if (checkSessionTimeout(lastAct)) {
        logout();
        return;
      }
    };

    const interval = setInterval(checkTimeout, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [session, logout]);

  // Activity tracker
  useEffect(() => {
    if (!session) return;

    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      localStorage.setItem('lastActivity', now.toString());
    };

    // Update activity on user interactions
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [session]);

  useEffect(() => {
    console.log('[Auth] Initializing, demo mode:', isDemo);
    
    // If demo mode, create a demo user and skip auth
    if (isDemo) {
      const demoUser = {
        id: 'demo-user-123',
        email: 'demo@example.com',
        user_metadata: { first_name: 'Demo', last_name: 'User' },
        app_metadata: {},
        aud: 'authenticated',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        role: 'authenticated'
      } as User;
      
      setUser(demoUser);
      setSession({ 
        user: demoUser, 
        access_token: 'demo-token',
        token_type: 'bearer',
        expires_at: Date.now() + 3600000,
        expires_in: 3600,
        refresh_token: 'demo-refresh'
      } as Session);
      setLoading(false);
      return;
    }

    let mounted = true;

    const initializeAuth = async () => {
      try {
        // PRIMARY: Set up auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('[Auth] State change:', event, session ? '✓' : '✗');
            
            if (!mounted) return;
            
            // Handle token refresh failures - sign out immediately
            if (event === 'TOKEN_REFRESHED' && !session) {
              console.error('[Auth] Token refresh failed - signing out');
              await logout();
              return;
            }
            
            try {
              setSession(session);
              setUser(session?.user ?? null);
              
              // Clear stale data on signout or auth errors
              if (event === 'SIGNED_OUT' || !session) {
                const keysToRemove = [
                  'supabase.auth.token',
                  'lastActivity',
                  'sessionFingerprint',
                  'userActivities'
                ];
                keysToRemove.forEach(key => {
                  try {
                    localStorage.removeItem(key);
                  } catch (e) {
                    console.warn('Failed to remove localStorage key:', key);
                  }
                });
                setLoading(false);
              } else {
                // Enhanced session initialization
                const now = Date.now();
                setLastActivity(now);
                try {
                  localStorage.setItem('lastActivity', now.toString());
                } catch (e) {
                  console.warn('Failed to set lastActivity in localStorage');
                }
                
                // Initialize session security (non-blocking)
                try {
                  initializeSessionSecurity(session);
                } catch (secError) {
                  console.warn('Session security init failed:', secError);
                }
                
                // Validate session integrity (mark as initial auth to be more permissive)
                try {
                  if (!validateSession(true)) {
                    return; // Session validation failed, logout already called
                  }
                } catch (valError) {
                  console.warn('Session validation failed:', valError);
                }

                // Ensure profile exists IMMEDIATELY - no delays
                if (session?.user) {
                  ensureProfileExists(session.user).catch(error => {
                    console.error('Critical profile creation failure:', error);
                  });
                }
                
                setLoading(false);
              }
            } catch (error) {
              console.error('[Auth] State change error:', error);
              // Only sign out on critical errors, not all errors
              const errorMsg = error instanceof Error ? error.message : String(error);
              if (errorMsg.includes('JWT') || errorMsg.includes('expired') || errorMsg.includes('unauthorized')) {
                await logout();
              }
              setLoading(false);
            }
          }
        );

        // PRIMARY: Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('[Auth] Initial session:', session ? '✓' : '✗', error ? `Error: ${error.message}` : '');
        
        if (!mounted) return;
        
        try {
          if (error) {
            // Check if it's a refresh token error (400)
            const errorMessage = error.message?.toLowerCase() || '';
            if (
              errorMessage.includes('refresh') ||
              errorMessage.includes('token') ||
              errorMessage.includes('expired') ||
              errorMessage.includes('invalid')
            ) {
              console.warn('[Auth] Invalid/expired session - clearing and redirecting to login');
              await logout();
              return;
            }
            
            // Clear any stale auth data on error
            try {
              localStorage.removeItem('supabase.auth.token');
              localStorage.removeItem('lastActivity');
            } catch (e) {
              console.warn('Failed to clear localStorage on error');
            }
            setSession(null);
            setUser(null);
          } else {
            setSession(session);
            setUser(session?.user ?? null);
            
            if (session) {
              // Initialize activity tracking
              const now = Date.now();
              setLastActivity(now);
              try {
                localStorage.setItem('lastActivity', now.toString());
              } catch (e) {
                console.warn('Failed to set initial lastActivity');
              }

              // Ensure profile exists IMMEDIATELY - no delays
              ensureProfileExists(session.user).catch(error => {
                console.error('Critical profile creation failure:', error);
              });
            }
          }
        } catch (sessionError) {
          console.error('[Auth] Session processing error:', sessionError);
          // Only sign out on critical errors
          const errorMsg = sessionError instanceof Error ? sessionError.message : String(sessionError);
          if (errorMsg.includes('JWT') || errorMsg.includes('expired') || errorMsg.includes('unauthorized')) {
            await logout();
          }
        } finally {
          setLoading(false);
        }

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (authError) {
        console.error('Auth provider initialization error:', authError);
        setLoading(false);
      }
    };

    initializeAuth();
  }, [isDemo]);

  return (
    <AuthContext.Provider value={{ user, session, loading, logout, isDemo }}>
      {children}
    </AuthContext.Provider>
  );
};